import { useCallback } from 'react';

import { Helmet } from 'react-helmet-async';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { parseApiErrorMessage } from '@liscio/api';
import {
  ForgotPasswordResetV5ContentShared,
  useResetPasswordV5Process,
  validationSchema as resetPasswordValidationSchema,
} from '@liscio/common';
import { styled, Box, CenteredLayout } from '@liscio/ui';

import { Logo } from '../../LoginV5View/components';
import { Copyright } from '../../LoginView/components';
import { MfaCodeForm } from 'modules/authenticate/components';
import { AuthPasswordInput } from 'modules/authenticate/components/FormFields';
import { AUTHENTICATE_PAGE_TITLES } from 'modules/authenticate/route-constants';

const Wrapper = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  paddingBottom: '16px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  maxWidth: theme.breakpoints.values.sm,
  [theme.breakpoints.up('md')]: {
    color: theme.palette.text.primary,
  },
}));

export const ForgotPasswordResetV5View = () => {
  const { token } = useParams<{ token: string }>();
  // Back to login
  const navigate = useNavigate();
  const backToLogin = useCallback(
    () => navigate('/authenticate/login', { replace: true }),
    [navigate]
  );

  const { processState, resetPassword, errors, isLoading, validateMfaCode } =
    useResetPasswordV5Process({
      token: token ?? '',
      onProcessFinished: () => {
        toast.success('Password changed successfully', { duration: 3000 });
        backToLogin();
      },
    });

  return (
    <>
      <Helmet title={AUTHENTICATE_PAGE_TITLES.forgotPasswordReset} />
      <CenteredLayout>
        <Wrapper>
          <ForgotPasswordResetV5ContentShared
            logo={<Logo />}
            footer={<Copyright />}
            processState={processState}
            errors={errors}
            isLoading={isLoading}
            parseError={parseApiErrorMessage}
            onCancel={backToLogin}
            onResetPassword={resetPassword}
            onValidateMfaCode={validateMfaCode}
            PasswordInputComponent={AuthPasswordInput}
            MfaCodeFormComponent={MfaCodeForm}
            validationSchema={resetPasswordValidationSchema}
            contentTopMargin={2}
            footerTopMargin={2}
          />
        </Wrapper>
      </CenteredLayout>
    </>
  );
};
